import { useState, useEffect, useMemo, useRef, ReactNode } from 'react';
import { Box, Typography, CardMedia, IconButton } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Alert from '@mui/material/Alert';
import { ExternalLink } from '@/components/ExternalLink';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useLocalization } from '@/hooks/useLocalization';
import fallbackImage from '@/assets/images/consultation.png';
import { ContentContainer, ContentContainerVariant } from './contentContainer';

const isEmbeddableBundle = import.meta.env.VITE_WIDGET === 'external';

export interface ResourceCardRecord {
  UserExternalLinkId?: number;
  Name: string | null;
  Url: string;
  Description: string | null;
  Photo: string | null;
}

export interface ResourcesCarouselProps {
  records: ResourceCardRecord[] | undefined;
  isLoading: boolean;
  hasError: boolean;
  title: string;
  errorText: string;
  emptyText: string;
  isWidget?: boolean;
  container?: ContentContainerVariant;
  footer?: ReactNode;
}

export function ResourcesCarousel({
  records,
  isLoading,
  hasError,
  title,
  errorText,
  emptyText,
  isWidget,
  container = 'widget',
  footer,
}: ResourcesCarouselProps) {
  const { t } = useLocalization();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const descRef = useRef<HTMLElement | null>(null);

  const items = useMemo(() => {
    if (!records) return [];
    const occurrences = new Map<string, number>();
    return records.map((resource) => {
      const identity =
        resource.UserExternalLinkId === undefined
          ? `${resource.Url}|${resource.Name ?? ''}`
          : `id:${String(resource.UserExternalLinkId)}`;
      const occurrence = occurrences.get(identity) ?? 0;
      occurrences.set(identity, occurrence + 1);
      return {
        key: `${identity}|${String(occurrence)}`,
        title: resource.Name?.toUpperCase() || '',
        link: resource.Url,
        description: resource.Description || '',
        photo: resource.Photo || '',
      };
    });
  }, [records]);

  const selectedIndex = items.findIndex((item) => item.key === selectedKey);
  const activeIndex = Math.max(selectedIndex, 0);
  const currentItem =
    activeIndex < items.length ? items[activeIndex] : undefined;
  const activeKey = currentItem?.key ?? null;

  useEffect(() => {
    setSelectedKey(activeKey);
    setIsExpanded(false);
  }, [activeKey]);

  const showItem = (index: number) => {
    setSelectedKey(items[index].key);
    setIsExpanded(false);
  };

  const handlePrev = () => {
    showItem(activeIndex === 0 ? items.length - 1 : activeIndex - 1);
  };

  const handleNext = () => {
    showItem(activeIndex === items.length - 1 ? 0 : activeIndex + 1);
  };

  /**
   * Whenever we switch items, collapse again or the card resizes,
   * check if the description text is truly truncated.
   */
  useEffect(() => {
    const el = descRef.current;
    if (!el || isExpanded) {
      setIsTruncated(false);
      return;
    }
    const measure = () => {
      const threshold = 1;
      setIsTruncated(el.scrollHeight - el.clientHeight > threshold);
    };
    const frame = requestAnimationFrame(measure);
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        cancelAnimationFrame(frame);
      };
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [currentItem, isExpanded]);

  const renderMainContent = () => {
    if (isLoading) {
      return <LoadingSpinner />;
    }
    if (hasError) {
      return <Alert severity='error'>{errorText}</Alert>;
    }
    if (!records?.length || !currentItem) {
      return <Typography>{emptyText}</Typography>;
    }

    const imageSrc =
      currentItem.photo || (!isEmbeddableBundle ? fallbackImage : '');

    return (
      <ExternalLink
        url={currentItem.link}
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3,
          width: '100%',
        }}
      >
        {imageSrc && (
          <CardMedia
            component='img'
            image={imageSrc}
            alt={currentItem.title}
            onError={(e) => {
              e.currentTarget.onerror = null; // Prevent infinite loop
              if (isEmbeddableBundle) {
                e.currentTarget.removeAttribute('src');
                return;
              }
              e.currentTarget.src = fallbackImage;
            }}
            sx={{
              maxHeight: 150,
              objectFit: 'contain',
            }}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <Typography
            component='div'
            variant='subtitle1'
          >
            {currentItem.title}
          </Typography>

          <Typography
            ref={descRef}
            variant='body2'
            color='text.secondary'
            sx={
              !isExpanded
                ? {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }
                : {}
            }
          >
            {currentItem.description}
          </Typography>

          {/* "Read More" / "Read Less" – only if truncated or expanded */}
          {(isTruncated || isExpanded) && (
            <Typography
              variant='body2'
              sx={{
                mt: 1,
                textDecoration: 'underline',
                cursor: 'pointer',
                color: 'primary.main',
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsExpanded((prev) => !prev);
              }}
            >
              {isExpanded ? t('readLess') : t('readMore')}
            </Typography>
          )}
        </Box>
      </ExternalLink>
    );
  };

  return (
    <ContentContainer
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isWidget ? 'fit-content' : '100%',
      }}
      container={container}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='h5'>{title}</Typography>
          {!!records?.length && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconButton onClick={handlePrev}>
                <KeyboardArrowLeftIcon />
              </IconButton>
              <Typography>
                {activeIndex + 1}/{items.length}
              </Typography>
              <IconButton onClick={handleNext}>
                <KeyboardArrowRightIcon />
              </IconButton>
            </Box>
          )}
        </Box>
        {/* Main Content Area */}
        {renderMainContent()}
        {footer}
      </Box>
    </ContentContainer>
  );
}
